import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { posthog, posthogLoaded } from "@/lib/posthog";

import {
  LANG_LABELS,
  LANG_SHORT,
  SUPPORTED_LANGS,
  normalizeLang,
  type Lang,
} from "@/i18n/config";
import { cn } from "@/lib/utils";

/** Selector de idioma para la cabecera pública. Sin dependencias de Radix:
 *  botón + menú posicionado con cierre al hacer clic fuera / Escape. */
export function LanguageSelector({ solid }: { solid: boolean }) {
  const { i18n, t } = useTranslation();
  const current = normalizeLang(i18n.language);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const choose = (lng: Lang) => {
    if (posthogLoaded() && lng !== current) posthog().capture("language_changed", { lang: lng });
    void i18n.changeLanguage(lng);
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t("nav.language")}
        className={cn(
          // Exploración: mismo texto y color que el selector de ciudad (sin
          // opacidad: al 70 % se veía gris al lado de «VLC»).
          "inline-flex items-center gap-1 rounded px-0.5 py-1 text-sm transition-opacity duration-150 hover:opacity-70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rc-violet active:scale-95",
          solid ? "text-rc-navy" : "text-white"
        )}
      >
        <GlobeIcon />
        <span className="tabular-nums">{LANG_SHORT[current]}</span>
      </button>

      {open && (
        // Exploración: mismo desplegable que las opciones de la frase de la cartelera.
        <div className="absolute right-0 top-full z-50 mt-2 flex w-max max-w-[20rem] flex-col gap-3 border-2 border-rc-navy bg-white p-4">
          <span className="data-voice text-[11px] font-bold text-rc-navy/70">{t("nav.language")}</span>
          <ul
            role="listbox"
            aria-label={t("nav.language")}
            className="flex flex-wrap gap-2"
          >
            {SUPPORTED_LANGS.map((lng) => {
              const active = lng === current;
              return (
                <li key={lng}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={active}
                    onClick={() => choose(lng)}
                    className={cn(
                      "data-voice rounded-full border-2 border-rc-navy px-3.5 py-1.5 text-xs font-bold transition-[transform,background-color,color] duration-150 ease-spring active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rc-violet",
                      active ? "bg-rc-navy text-white" : "bg-white text-rc-navy hover:bg-rc-lime"
                    )}
                  >
                    {LANG_LABELS[lng]}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

function GlobeIcon() {
  return (
    <svg
      className="h-4 w-4"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18" />
      <path d="M12 3a14 14 0 0 1 0 18 14 14 0 0 1 0-18Z" />
    </svg>
  );
}
